import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { ContentProvider } from "@/context/ContentContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { initLenis } from "@/lib/lenis";
import HomePage from "@/pages/HomePage";
import { HOTELS_SECTION_ENABLED } from "@/lib/sectionFlags";
import { CompareProvider } from "@/context/CompareContext";
import { CompareTray } from "@/components/offers/CompareOffers";
import { loadFeatureFlags } from "@/lib/featureFlags";

const MaldivesPage = lazy(() => import("@/pages/MaldivesPage"));
const ResortPage = lazy(() => import("@/pages/ResortPage"));
const ResortQuotePage = lazy(() => import("@/pages/ResortQuotePage"));
const ProgramsPage = lazy(() => import("@/pages/ProgramsPage"));
const CountryPage = lazy(() => import("@/pages/CountryPage"));
const ProgramPage = lazy(() => import("@/pages/ProgramPage"));
const PlanTripPage = lazy(() => import("@/pages/PlanTripPage"));
const FlightQuotePage = lazy(() => import("@/pages/FlightQuotePage"));
const OffersPage = lazy(() => import("@/pages/OffersPage"));
const OfferPage = lazy(() => import("@/pages/OfferPage"));
const OfferPreviewPage = lazy(() => import("@/pages/OfferPreviewPage"));
const TripPlanPage = lazy(() => import("@/pages/TripPlanPage"));
const HotelsPage = lazy(() => import("@/pages/HotelsPage"));
const HotelsCountryPage = lazy(() => import("@/pages/HotelsCountryPage"));
const HotelsCityPage = lazy(() => import("@/pages/HotelsCityPage"));
const HotelsComingSoon = lazy(() => import("@/pages/HotelsComingSoon"));
const HotelDetailPage = lazy(() => import("@/pages/HotelDetailPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const FloatingWhatsApp = lazy(() =>
  import("@/components/FloatingWhatsApp").then((m) => ({ default: m.FloatingWhatsApp }))
);

const RouteFallback = (
  <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center" data-testid="route-loading">
    <div className="h-10 w-10 rounded-full border-2 border-[#C80000]/30 border-t-[#C80000] animate-spin" />
  </div>
);

function App() {
  useEffect(() => {
    loadFeatureFlags();
    const destroy = initLenis();
    return destroy;
  }, []);

  return (
    <ContentProvider>
      <FavoritesProvider>
        <CompareProvider>
        <BrowserRouter>
        <Suspense fallback={RouteFallback}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/maldives" element={<MaldivesPage />} />
          <Route path="/maldives/:slug/request" element={<ResortQuotePage />} />
          <Route path="/maldives/:slug" element={<ResortPage />} />
          <Route path="/programs" element={<ProgramsPage />} />
          <Route path="/programs/:country" element={<CountryPage />} />
          <Route path="/programs/:country/:slug" element={<ProgramPage />} />
          <Route path="/plan-trip" element={<PlanTripPage />} />
          <Route path="/flight-quote" element={<FlightQuotePage />} />
          <Route path="/offers" element={<OffersPage />} />
          <Route path="/offers/preview/:id" element={<OfferPreviewPage />} />
          <Route path="/offers/:slug" element={<OfferPage />} />
          <Route path="/trip-plans/:slug" element={<TripPlanPage />} />
          <Route path="/hotels" element={HOTELS_SECTION_ENABLED ? <HotelsPage /> : <HotelsComingSoon />} />
          <Route path="/hotels/:countrySlug" element={HOTELS_SECTION_ENABLED ? <HotelsCountryPage /> : <HotelsComingSoon />} />
          <Route path="/hotels/:countrySlug/:citySlug" element={HOTELS_SECTION_ENABLED ? <HotelsCityPage /> : <HotelsComingSoon />} />
          <Route path="/hotels/:countrySlug/:citySlug/:hotelSlug" element={HOTELS_SECTION_ENABLED ? <HotelDetailPage /> : <HotelsComingSoon />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        <Suspense fallback={null}>
          <FloatingWhatsApp />
        </Suspense>
        <CompareTray />
        <Toaster position="top-center" richColors />
        </BrowserRouter>
        </CompareProvider>
      </FavoritesProvider>
    </ContentProvider>
  );
}

export default App;
